import { useCallback, useRef } from "react";
import {
    startRecording as startRecordingRequest,
    uploadRecordingChunk,
    completeRecording,
} from "../Requests/QuestionnaireRequests";

/**
 * Chunked, resumable upload for anything MediaRecorder produces.
 *
 * Extracted from useQuestionnaireRecording so voice answers get the same
 * guarantees the session recording already had. Voice answers used to buffer
 * the whole take in the tab and POST it as one blob, which is why they were
 * capped at 30 minutes and why a dropped connection lost the recording.
 *
 * The invariants that matter:
 *  - Nothing accumulates. Every timeslice goes straight into a bounded queue.
 *  - Chunks upload strictly in order. MediaRecorder chunks after the first are
 *    not standalone containers, so the server rejects anything out of sequence.
 *  - A replayed index is a server-side no-op, which is what makes retry safe.
 *  - Backpressure pauses the recorder rather than growing the queue without
 *    bound: a gap in the capture beats losing the rest of a long session.
 */

/** Emit a chunk every 10s: bounds worst-case data loss to 10s. */
export const TIMESLICE_MS = 10_000;

const PAUSE_AT = 6;
const RESUME_AT = 2;
const MAX_PENDING_CHUNKS = 20;

const RETRY_DELAYS_MS = [2_000, 6_000, 18_000];

/**
 * A chunk upload that hangs (TCP blackhole after a wifi roam or laptop sleep)
 * never rejects on its own, which would stall the drain loop forever — the
 * failure mode that killed long sessions. Abort and let the retry loop own it.
 */
const CHUNK_TIMEOUT_MS = 30_000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function createUploadEntry(kind, stream, recordingUuid) {
    return {
        kind,
        stream,
        recordingUuid,
        queue: [],
        nextIndex: 0,
        uploadedIndex: 0,
        drainPromise: null,
        paused: false,
        stopped: false,
        corrupt: false,
        finalizePending: false,
        // Set by finishTrack when the caller measured one. Screen/camera
        // recordings do not, and the server falls back to the chunk count.
        durationSeconds: null,
    };
}

/**
 * @param onDegraded called with a message when a recording can no longer be
 *   completed cleanly; the caller decides how to surface it.
 */
export default function useChunkedRecorderUpload({ onDegraded } = {}) {
    const onDegradedRef = useRef(onDegraded);
    onDegradedRef.current = onDegraded;

    const degrade = useCallback((message) => {
        onDegradedRef.current?.(message);
    }, []);

    const stopTrack = useCallback((entry, { finalize = true } = {}) => {
        if (!entry || entry.stopped) return;
        entry.stopped = true;

        try {
            if (entry.recorder && entry.recorder.state !== "inactive") {
                entry.recorder.stop();
            }
        } catch (e) {
            /* recorder already torn down */
        }

        entry.stream?.getTracks().forEach((track) => track.stop());

        if (finalize && entry.recordingUuid) {
            entry.finalizePending = true;
        }
    }, []);

    const drain = useCallback((entry) => {
        // Callers can await the returned promise and get the real end of the
        // drain, not an early return — stop() depends on that to know the tail
        // of the recording actually reached the server.
        if (entry.drainPromise) return entry.drainPromise;

        const run = async () => {
            while (entry.queue.length > 0) {
                const chunk = entry.queue[0];
                let delivered = false;

                for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
                    const controller = new AbortController();
                    const timer = setTimeout(() => controller.abort(), CHUNK_TIMEOUT_MS);
                    try {
                        await uploadRecordingChunk(entry.recordingUuid, chunk.index, chunk.blob, controller.signal);
                        delivered = true;
                        break;
                    } catch (e) {
                        // The server acknowledges an already-stored chunk, so a 409
                        // means we are genuinely out of step — resyncing is pointless
                        // mid-stream, so give up on this recording rather than
                        // uploading bytes that would corrupt the file.
                        if (e?.response?.status === 409) {
                            entry.corrupt = true;
                            break;
                        }
                        if (attempt < RETRY_DELAYS_MS.length) {
                            await sleep(RETRY_DELAYS_MS[attempt]);
                        }
                    } finally {
                        clearTimeout(timer);
                    }
                }

                if (entry.corrupt) {
                    entry.queue = [];
                    stopTrack(entry, { finalize: false });
                    degrade("Recording stopped: the upload fell out of sync.");
                    break;
                }

                if (!delivered) {
                    // Still failing after the retry budget. Leave it queued; the next
                    // chunk's arrival retries the whole queue.
                    break;
                }

                entry.queue.shift();
                entry.uploadedIndex = chunk.index + 1;

                // Backpressure cleared: pick the capture back up.
                if (entry.paused && entry.queue.length <= RESUME_AT && !entry.stopped) {
                    entry.paused = false;
                    try {
                        if (entry.recorder?.state === "paused") entry.recorder.resume();
                    } catch (e) {
                        /* recorder already torn down */
                    }
                }
            }

            if (entry.finalizePending && entry.queue.length === 0) {
                entry.finalizePending = false;
                try {
                    await completeRecording(entry.recordingUuid, entry.durationSeconds ?? null);
                    entry.completed = true;
                } catch (e) {
                    /* the sweeper reclaims the temp file if this never lands */
                }
            }
        };

        entry.drainPromise = run().finally(() => {
            entry.drainPromise = null;
            // Keep retrying while live: with the recorder paused on backpressure
            // no new chunk will arrive to retrigger the drain, so it must re-arm
            // itself or an outage becomes a deadlock. After stop(), its own
            // bounded loop drives draining instead.
            if (entry.queue.length > 0 && !entry.stopped) drain(entry);
        });

        return entry.drainPromise;
    }, [degrade, stopTrack]);

    const enqueue = useCallback((entry, blob) => {
        if (!blob || blob.size === 0 || entry.corrupt) return;

        if (entry.queue.length >= MAX_PENDING_CHUNKS) {
            // Safety net the pause below should make unreachable. If we still
            // get here, stop cleanly and keep what already uploaded.
            degrade("Recording stopped: the connection was lost for too long.");
            stopTrack(entry, { finalize: true });
            return;
        }

        entry.queue.push({ index: entry.nextIndex, blob });
        entry.nextIndex += 1;

        // Falling behind: pause the capture instead of letting the queue grow
        // to the kill threshold. A gap in the recording beats losing the rest
        // of a long session; drain() resumes once the backlog clears.
        if (!entry.paused && !entry.stopped && entry.queue.length >= PAUSE_AT) {
            entry.paused = true;
            try {
                if (entry.recorder?.state === "recording") entry.recorder.pause();
            } catch (e) {
                /* recorder already torn down */
            }
        }

        drain(entry);
    }, [degrade, drain, stopTrack]);

    /** Open a recording server-side and wire a MediaRecorder straight into the queue. */
    const beginTrack = useCallback(async (
        responseUuid,
        kind,
        stream,
        { mimeType, videoBitsPerSecond, audioBitsPerSecond, questionUuid } = {}
    ) => {
        let recordingUuid;
        try {
            const res = await startRecordingRequest(
                responseUuid,
                kind,
                mimeType || (kind === "voice" ? "audio/webm" : "video/webm"),
                questionUuid
            );
            recordingUuid = res.data.recording_uuid;
        } catch (e) {
            stream.getTracks().forEach((track) => track.stop());
            return null;
        }

        const entry = createUploadEntry(kind, stream, recordingUuid);

        const recorder = new MediaRecorder(stream, {
            ...(mimeType ? { mimeType } : {}),
            ...(videoBitsPerSecond ? { videoBitsPerSecond } : {}),
            ...(audioBitsPerSecond ? { audioBitsPerSecond } : {}),
        });

        // Straight to the queue. Never into an array that outlives this call —
        // that accumulation is precisely what makes long recordings fail.
        recorder.ondataavailable = (event) => enqueue(entry, event.data);
        recorder.onstop = () => drain(entry);

        entry.recorder = recorder;
        recorder.start(TIMESLICE_MS);

        return entry;
    }, [drain, enqueue]);

    /**
     * Stop and wait for the queue to genuinely empty, including a drain that was
     * already running — resolving before the tail chunks and completeRecording()
     * land is how a closed tab used to eat the end of a long recording.
     *
     * Bounded: each pass carries the full per-chunk retry budget, so three
     * fruitless passes means the connection is properly dead.
     */
    const finishTrack = useCallback(async (entry, { durationSeconds } = {}) => {
        if (!entry) return false;

        // Recorded on the entry so the drain loop can send it with complete(),
        // whichever pass ends up finalising.
        if (durationSeconds != null) entry.durationSeconds = durationSeconds;

        stopTrack(entry, { finalize: true });

        // stop() emits one last dataavailable; give it a tick to enqueue before
        // draining, so the tail of the recording is not dropped.
        await sleep(50);

        for (let pass = 0; pass < 3; pass += 1) {
            if (entry.queue.length === 0 && !entry.drainPromise && !entry.finalizePending) break;
            await (entry.drainPromise || drain(entry));
        }

        return entry.queue.length === 0 && !entry.corrupt;
    }, [drain, stopTrack]);

    return { beginTrack, finishTrack, stopTrack, drain, enqueue };
}
